import React, { useEffect, useState, useMemo } from "react";

import { Divider, Grow } from "@mui/material";
import { DespatchTaskFilters, DespatchTaskPublishFilters } from "../DespatchFilters";
import { useOperationDespatchContext } from "../../../../contexts/DespatchContext";
import { DiaryType } from "Components/DiaryCalendar";
import { useAppState } from "Context/StateContext";
import { useTranslation } from "react-i18next";
import { ResourceSubGroup } from "Util/enums";
import {
  checkResourceCan,
  FN_NAMES,
  hasPermission,
  isShowAction,
  operationsPermissions,
} from "Util/permissions";

import { DiaryHeaderCreationMenu } from "./DiaryHeaderCreationMenu";
import { DiaryHeaderDateSection } from "./DiaryHeaderDateSection";
import { DiaryHeaderFullscreenCloseButton } from "./DiaryHeaderFullscreenCloseButton";
import { DiaryHeaderFullscreenToogleButton } from "./DiaryHeaderFullscreenToogleButton";
import { DiaryHeaderSearchButton } from "./DiaryHeaderSearchButton";
import { DiaryHeaderSettingsMenu } from "./DiaryHeaderSettingsMenu";
import { DiaryHeaderTitle } from "./DiaryHeaderTitle";
import { DiaryPrintReports } from "./DiaryPrintReports";
import { Container, Section } from "./styled";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { isMoving } from "Util/validation";
import Button, { Color } from "Components/Form/Button";
import SaveIcon from "Components/Generic/Icons/SaveIcon";

export interface DiaryHeaderProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  title?: string;
}

export const DiaryHeader = (props: DiaryHeaderProps) => {
  const { currentUser, userPermissions } = useAppState();
  const { title, ...rest } = props;
  const {
    isSearchMode,
    isFullscreen,
    viewMode,
    changesMade,
    persistCrews,
    isLoadingPersistCrewsChanges,
  } = useOperationDespatchContext();

  const { t: translate } = useTranslation();
  const [dispatchPermission, setDispatchPermission] = useState<{
    create: {
      team: boolean;
      task: boolean;
      placeholder: boolean;
    };
    setting: boolean;
    print: boolean;
    search: boolean;
  }>({
    create: {
      team: false,
      task: false,
      placeholder: false,
    },
    setting: false,
    print: false,
    search: false,
  });

  const showSaveChangesButton = useMemo(() => {
    return viewMode === DiaryType.RESOURCES && !!changesMade;
  }, [viewMode, changesMade]);

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setDispatchPermission({
        create: {
          team:
            checkResourceCan(userPermissions, ResourceName.DISPATCH, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_NEW_TEAM),
          task:
            checkResourceCan(userPermissions, ResourceName.DISPATCH, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_NEW_TASK),
          placeholder:
            checkResourceCan(userPermissions, ResourceName.DISPATCH, ResourceCanEnum.EDIT) &&
            hasPermission(
              userPermissions,
              FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_NEW_PLACEHOLDER_TASK
            ),
        },
        setting:
          checkResourceCan(userPermissions, ResourceName.DISPATCH, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_SETTINGS),
        print:
          checkResourceCan(userPermissions, ResourceName.DISPATCH, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_PRINT_REPORT),
        search: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_SEARCH),
      });
    }
  }, [userPermissions]);

  const isEnableToCreateTaskOrTeam = operationsPermissions({
    subGroup: ResourceSubGroup.DISPATCH,
    resourceName: ResourceName.DISPATCH,
    currentUser,
  })?.add;

  const renderSaveChangesButton = () => {
    if (!showSaveChangesButton) return null;

    return (
      <Section flex={1}>
        <Button
          customColor={Color.BRAND_OUTLINED}
          startIcon={
            isLoadingPersistCrewsChanges ? null : (
              <SaveIcon width={20} height={20} color="#0055A4" />
            )
          }
          onClick={() => persistCrews()}
          loadingColor="#0055A4"
          loading={isLoadingPersistCrewsChanges}
        >
          {translate("saveChanges")}
        </Button>
      </Section>
    );
  };

  const renderSearchContent = () => {
    if (!isSearchMode) return <div />;

    return (
      <Container {...props}>
        <Section>
          <DiaryHeaderSearchButton />

          <DiaryHeaderTitle title={translate("search")} />

          <DespatchTaskFilters />
        </Section>

        {isFullscreen && <DiaryHeaderFullscreenCloseButton />}
      </Container>
    );
  };

  const renderNormalContent = () => {
    if (isSearchMode) return <div />;

    if (isFullscreen) {
      return (
        <Container isFullscreen={isFullscreen} {...rest}>
          <Section>
            {title && <DiaryHeaderTitle title={title} />}

            {viewMode !== DiaryType.LIST && <DiaryHeaderDateSection />}

            <DiaryHeaderFullscreenCloseButton />
          </Section>

          <Section>
            <Section flex={showSaveChangesButton ? undefined : 1}>
              <DespatchTaskPublishFilters />
            </Section>

            {renderSaveChangesButton()}

            <Section gap="12px">
              {isShowAction(currentUser, dispatchPermission.search) && <DiaryHeaderSearchButton />}

              {isShowAction(currentUser, dispatchPermission.print) && <DiaryPrintReports />}

              <DiaryHeaderFullscreenToogleButton />

              {isShowAction(currentUser, dispatchPermission.setting) && <DiaryHeaderSettingsMenu />}

              {!isMoving(currentUser?.company) && !isEnableToCreateTaskOrTeam ? null : (
                <DiaryHeaderCreationMenu createPermission={dispatchPermission.create} />
              )}
            </Section>
          </Section>
        </Container>
      );
    }

    return (
      <Container isFullscreen={isFullscreen} {...props}>
        <Section>
          {title && <DiaryHeaderTitle title={title} />}

          {viewMode !== DiaryType.LIST && <DiaryHeaderDateSection />}

          <DiaryHeaderFullscreenCloseButton />
        </Section>

        {viewMode === DiaryType.RESOURCES && <Divider orientation="vertical" flexItem />}

        <Section flex={showSaveChangesButton ? undefined : 1}>
          <DespatchTaskPublishFilters />
        </Section>

        {renderSaveChangesButton()}

        <Section gap="12px">
          {isShowAction(currentUser, dispatchPermission.search) && <DiaryHeaderSearchButton />}

          {isShowAction(currentUser, dispatchPermission.print) && <DiaryPrintReports />}

          <DiaryHeaderFullscreenToogleButton />

          {isShowAction(currentUser, dispatchPermission.setting) && <DiaryHeaderSettingsMenu />}

          {!isMoving(currentUser?.company) && !isEnableToCreateTaskOrTeam ? null : (
            <DiaryHeaderCreationMenu createPermission={dispatchPermission.create} />
          )}
        </Section>
      </Container>
    );
  };

  return (
    <>
      <Grow in={!isSearchMode}>{renderNormalContent()}</Grow>

      <Grow in={isSearchMode}>{renderSearchContent()}</Grow>
    </>
  );
};
